// Location: src/pages/plan-summary/components/SummaryStats.tsx

import { useTranslation } from 'react-i18next';

interface SummaryStatsProps {
  summary: {
    start_weight: number;
    end_weight: number;
    weight_change: number;
    days_completed: number;
    total_days: number;
    meals_logged: number;
    total_meals: number;
    avg_calories: number;
    avg_protein: number;
    avg_carbs: number;
    avg_fat: number;
  };
}

export default function SummaryStats({ summary }: SummaryStatsProps) {
  const { t } = useTranslation();

  const stats = [
    {
      icon: 'ri-scales-3-line',
      label: t('planSummary.stats.startWeight'),
      value: `${summary.start_weight.toFixed(1)} kg`,
      color: 'text-gray-400',
      iconBg: 'bg-gray-500/10'
    },
    {
      icon: 'ri-scales-line',
      label: t('planSummary.stats.endWeight'),
      value: `${summary.end_weight.toFixed(1)} kg`,
      color: summary.weight_change < 0 ? 'text-green-400' : 'text-blue-400',
      iconBg: summary.weight_change < 0 ? 'bg-green-500/10' : 'bg-blue-500/10'
    },
    {
      icon: 'ri-calendar-check-line',
      label: t('planSummary.stats.daysCompleted'),
      value: `${summary.days_completed}/${summary.total_days}`,
      color: 'text-yellow-400',
      iconBg: 'bg-yellow-500/10'
    },
    {
      icon: 'ri-restaurant-line',
      label: t('planSummary.stats.mealsLogged'),
      value: `${summary.meals_logged}/${summary.total_meals}`,
      color: 'text-orange-400',
      iconBg: 'bg-orange-500/10'
    },
    {
      icon: 'ri-fire-line',
      label: t('planSummary.stats.avgCalories'),
      value: `${Math.round(summary.avg_calories)} kcal`,
      color: 'text-red-400',
      iconBg: 'bg-red-500/10'
    },
    {
      icon: 'ri-heart-pulse-line',
      label: t('planSummary.stats.avgProtein'),
      value: `${Math.round(summary.avg_protein)}g`,
      color: 'text-purple-400',
      iconBg: 'bg-purple-500/10'
    },
    {
      icon: 'ri-cake-line',
      label: t('planSummary.stats.avgCarbs'),
      value: `${Math.round(summary.avg_carbs)}g`,
      color: 'text-blue-400',
      iconBg: 'bg-blue-500/10'
    },
    {
      icon: 'ri-drop-line',
      label: t('planSummary.stats.avgFat'),
      value: `${Math.round(summary.avg_fat)}g`,
      color: 'text-yellow-400',
      iconBg: 'bg-yellow-500/10'
    }
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {stats.map((stat, index) => (
        <div
          key={index}
          className="relative bg-white/[0.02] border border-white/5 rounded-2xl p-5 hover:bg-white/[0.04] transition-colors duration-300 group overflow-hidden"
        >
          {/* Subtle hover glow effect */}
          <div className={`absolute top-0 right-0 w-16 h-16 ${stat.iconBg} rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500 opacity-50`}></div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-8 h-8 rounded-full ${stat.iconBg} flex items-center justify-center`}>
                <i className={`${stat.icon} ${stat.color} text-lg`}></i>
              </div>
            </div>
            <div className="text-gray-500 text-[10px] sm:text-xs uppercase tracking-widest font-medium mb-1 truncate">
              {stat.label}
            </div>
            <div className="text-white text-2xl font-light tracking-tight">
              {stat.value}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}